import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Restores scroll position per-route for data-dependent pages.
 *
 * Native browser restore fails on async SPA pages: on back-nav the page
 * remounts short (Loader), browser restores against that short height, then
 * data loads and pushes content down — position lost.
 *
 * Fix: save scrollY to sessionStorage keyed by pathname, and restore ONLY
 * after the page content is actually rendered.
 *
 * @param {boolean} ready  Pass `!isLoading` — restore fires once this is true.
 *
 * Usage:
 *   const [isLoading, setLoading] = useState(true);
 *   useScrollRestoration(!isLoading);
 */
export default function useScrollRestoration(ready = true) {
    const { pathname } = useLocation();
    const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
    const key = `scroll-pos:${pathname}`;
    const restored = useRef(false);

    // Kill browser auto-restore — it conflicts with manual SPA restore.
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    // New route in the same component (e.g. /product/categories/a → /b):
    // allow restore/scroll-to-top to run again for the new key.
    useEffect(() => {
        restored.current = false;
    }, [key]);

    // Save scrollY (rAF-throttled). Only after restore done, so a stray
    // scroll=0 on mount can't overwrite the saved value.
    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (!restored.current || ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                sessionStorage.setItem(key, String(window.scrollY));
                ticking = false;
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [key]);

    // Restore once content is ready.
    useEffect(() => {
        if (!ready || restored.current) return;
        restored.current = true;

        // Back/forward nav → restore saved position.
        if (navType === 'POP') {
            const saved = sessionStorage.getItem(key);
            if (saved !== null) {
                requestAnimationFrame(() => window.scrollTo({ top: parseInt(saved, 10), left: 0, behavior: 'instant' }));
                return;
            }
        }
        // Fresh nav (link click / replace) → start at top.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [ready, key, navType]);
}
